/**
 * Helpers for keeping line-item color/free-text selections canonical (#1431).
 *
 * ColorSelectionMenu emits one combined array containing both real color
 * selections (colorId > 0) and free-text entries (text set, colorId 0 or
 * absent). Line items store these in two separate arrays — colorSelections
 * and freeTextSelections — and the print templates merge both for display,
 * so a free-text entry left in colorSelections prints twice.
 */

type MenuSelection = {
	colorId?: number | string;
	colorOptionId?: number;
	text?: string;
	upCharge?: number | null;
};

export interface FreeTextSelection {
	colorOptionId: number;
	text: string;
}

/**
 * Split the ColorSelectionMenu's combined output into the line item's two
 * canonical arrays. Free texts are normalized to {colorOptionId, text};
 * rows with neither a color nor text (untouched menu defaults) are dropped.
 */
export function splitColorMenuSelections<T extends MenuSelection>(selections?: T[] | null): {
	colorSelections: T[];
	freeTextSelections: FreeTextSelection[];
} {
	const colorSelections: T[] = [];
	const freeTextSelections: FreeTextSelection[] = [];

	for (const selection of selections || []) {
		const hasColor = typeof selection.colorId === "number" && selection.colorId > 0;
		if (hasColor) {
			colorSelections.push(selection);
		} else if (selection.text && selection.colorOptionId != null) {
			freeTextSelections.push({
				colorOptionId: selection.colorOptionId,
				text: selection.text,
			});
		}
	}

	return { colorSelections, freeTextSelections };
}

/**
 * Drop content-identical selections from a merged display list, keeping the
 * first occurrence. Guards the print templates against the same free text
 * arriving via both colorSelections and freeTextSelections.
 */
export function dedupeSelections<T extends MenuSelection>(selections: T[]): T[] {
	const seen = new Set<string>();
	return selections.filter((selection) => {
		const key = `${selection.colorOptionId ?? ""}|${selection.colorId || ""}|${selection.text || ""}`;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}
